import React, { useState, useEffect, useContext } from "react";
import * as Sentry from "@sentry/react";
import {
  findAccountType,
  findAccountsGroupInHierarchy,
  findIfSubAccountInHierarchy,
} from "./utils";
import {
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  FormControl,
  Autocomplete,
  Tooltip,
  Switch,
} from "@mui/material";
import Button from "../../../components/Button";
import { TitleText } from "../../component_styles";
import { LineText } from "../table_styles";
import UserContext from "../../../assets/user_context";
import StartingBalance from "./starting_balance";
import {
  useAccountNumberValidation,
  validateAccountType,
  validateAccountNumber,
  validateAccountName,
  validateGroup,
  validateSubAccount,
  validateStartingBalance,
  toCents,
} from "./validation";
import { useSubmitAccount } from "./add_to_db_logic";
import { isExpectedAccountError } from "./expectedErrors";
import LoadingLogo from "../../../assets/logos/animated-logo.svg?react";
import styled from "@emotion/styled";

const LoadingLogoStyled = styled(LoadingLogo)`
  z-index: 5;
  width: 50%;
`;

const accountTypes = ["Assets", "Liabilities", "Income", "Expenses"];

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const AddAccount = React.forwardRef(
  (
    {
      accountNumberToAdd,
      possibleAccountNameToAdd,
      setShowModal,
      org,
      db,
      massAdd,
      addGroup,
      groupsAddedToParent,
      thisGroup,
      options,
      accountDataToEdit,
      disabledAddButton,
      reenableSubmit,
      clickedAdd,
    },
    ref,
  ) => {
    //if reenableSubmit is not a function, set it to an empty function
    if (typeof reenableSubmit !== "function") {
      reenableSubmit = () => {};
    }
    // TODO: Add default fund to accounts, should be able to be changed later, and maybe someday calculate from their most used fund for this transaction
    const { funds, accounts, accountsHierarchy } = useContext(UserContext);
    const [accountEntryData, setAccountEntryData] = useState({
      type: accountNumberToAdd
        ? capitalizeFirstLetter(findAccountType(accountNumberToAdd))
        : options?.type
          ? options.type
          : accountDataToEdit?.accountType
            ? accountDataToEdit.accountType
            : "",
      accountName: possibleAccountNameToAdd
        ? possibleAccountNameToAdd
        : accountDataToEdit?.accountName
          ? accountDataToEdit.accountName
          : "",
      accountNumber: accountNumberToAdd
        ? accountNumberToAdd
        : accountDataToEdit?.accountNumber
          ? accountDataToEdit.accountNumber
          : "",
      isRegister: accountDataToEdit?.isRegister
        ? accountDataToEdit.isRegister
        : false,
      illiquid: accountDataToEdit?.illiquid
        ? accountDataToEdit.illiquid
        : false,
      isPayableReceivable: accountDataToEdit?.isPayableReceivable
        ? accountDataToEdit.isPayableReceivable
        : false,
      group: thisGroup
        ? thisGroup
        : options?.group
          ? options.group
          : accountDataToEdit?.group
            ? accountDataToEdit.group
            : "",
      subAccount: accountDataToEdit?.subAccount
        ? accountDataToEdit.subAccount
        : false,
      subAccountOf: accountDataToEdit?.subAccountOf
        ? accountDataToEdit.subAccountOf
        : null,
    });
    const matchOption = accountNumberToAdd && accounts.length > 0;
    const [accountEntryErrors, setAccountEntryErrors] = useState({
      type: false,
      accountName: false,
      accountNumber: false,
      group: false,
      subAccount: false,
    });
    const [accountEntryFeedback, setAccountEntryFeedback] = useState({
      type: "",
      accountName: "",
      accountNumber: "",
      group: "",
      subAccount: "",
    });
    const [existingAccountNumbers, setExistingAccountNumbers] = useState(
      accounts ? accounts.map((account) => String(account.accountNumber)) : [],
    );

    const [remainingStartingToAssign, setRemainingStartingToAssign] =
      useState(0);

    const editingAccountId = accountDataToEdit?.accountDatabaseId
      ? accountDataToEdit.accountDatabaseId
      : null;

    useAccountNumberValidation({
      number: accountEntryData.accountNumber,
      setAccountEntryErrors,
      setAccountEntryFeedback,
      existingAccountNumbers,
      accounts,
    });

    useEffect(() => {
      setExistingAccountNumbers(
        accounts ? accounts.map((account) => String(account.accountNumber)) : [],
      );
    }, [accounts]);

    const handleChange = (event) => {
      const { name, value, type, checked } = event.target;
      const inputValue = type === "checkbox" ? checked : value;

      if (name === "accountNumber" && value !== "") {
        if (/^[1-9][0-9]*\.?[0-9]*$/.test(inputValue)) {
          setAccountEntryData({
            ...accountEntryData,
            [name]: inputValue,
          });
        }
      } else {
        setAccountEntryData({
          ...accountEntryData,
          [name]: inputValue,
        });
      }
    };

    //TODO: This is a dumb workaround to the fact that the account number is not set on initial load
    useEffect(() => {
      if (accountDataToEdit) {
        setTimeout(() => {
          validateAccountNumber({
            accountEntryData,
            setAccountEntryErrors,
            setAccountEntryFeedback,
            accountDataToEdit,
            existingAccountNumbers,
          });
        }, 10);
      }
    }, []);

    //useEffect to validate subAccount if entryData changes and it is already an error
    useEffect(() => {
      if (accountEntryErrors.subAccount) {
        validateSubAccount({
          accountEntryData,
          setAccountEntryErrors,
          setAccountEntryFeedback,
        });
      }
      if (accountEntryErrors.startingBalance) {
        validateStartingBalance({
          accountEntryData,
          setRemainingStartingToAssign,
          setAccountEntryErrors,
          setAccountEntryFeedback,
        });
      }
    }, [accountEntryData]);

    // Validate all fields synchronously before allowing submission. Each
    // validator is a pure check over the CURRENT values that both pushes its
    // error/feedback flags (functional setState) and RETURNS its verdict, so we
    // compose the return values directly. The old implementation awaited no-op
    // promises and then read a lagging accountEntryErrorsRef, which let the
    // first mass-import submit pass invalid rows. (Duplicate-number checking is
    // intentionally omitted here — it was dead before and the inline hook /
    // handleSubmit re-check own it.)
    const validateForm = () => {
      const typeValid = validateAccountType({
        accountEntryData,
        setAccountEntryErrors,
        setAccountEntryFeedback,
      });
      const nameValid = validateAccountName({
        accountEntryData,
        setAccountEntryErrors,
        setAccountEntryFeedback,
      });
      const numberValid = validateAccountNumber({
        accountEntryData,
        setAccountEntryErrors,
        setAccountEntryFeedback,
        accountDataToEdit,
      });
      const groupValid = validateGroup({
        accountEntryData,
        setAccountEntryErrors,
        setAccountEntryFeedback,
      });

      return typeValid && nameValid && numberValid && groupValid;
    };

    const { handleSubmit } = useSubmitAccount({
      accountEntryData,
      editingAccountId,
      setShowModal,
      setAccountEntryData,
      accountDataToEdit,
      db,
      massAdd,
    });

    const handleClick = async () => {
      if (clickedAdd) clickedAdd();
      // Validate group
      validateGroup({
        accountEntryData,
        setAccountEntryErrors,
        setAccountEntryFeedback,
      });

      // Validate sub-account
      validateSubAccount({
        accountEntryData,
        setAccountEntryErrors,
        setAccountEntryFeedback,
      });

      // Reject a malformed/duplicate account number (e.g. a trailing dot like
      // "5000.") before submitting — handleClick otherwise only gates on
      // group/sub-account/starting-balance, so the format check must be here.
      const accountNumberValid = validateAccountNumber({
        accountEntryData,
        setAccountEntryErrors,
        setAccountEntryFeedback,
        accountDataToEdit,
        existingAccountNumbers,
      });
      if (!accountNumberValid) {
        reenableSubmit();
        return;
      }

      // Additional checks for starting balance
      if (accountEntryData.startingBalance > 0) {
        //Check for date
        if (!accountEntryData.startingBalanceDate) {
          setAccountEntryErrors((prev) => ({
            ...prev,
            startingBalanceDate: true,
          }));
          setAccountEntryFeedback((prev) => ({
            ...prev,
            startingBalanceDate: "Please enter a date",
          }));
          reenableSubmit();
        } else {
          setAccountEntryErrors((prev) => ({
            ...prev,
            startingBalanceDate: false,
          }));
          setAccountEntryFeedback((prev) => ({
            ...prev,
            startingBalanceDate: "",
          }));
        }
        const fundStartingBalances = Object.values(
          accountEntryData.fundStartingBalances,
        );
        const sumOfFundStartingBalances = fundStartingBalances.reduce(
          (acc, curr) => {
            if (curr !== "") {
              return acc + parseFloat(curr || 0);
            } else {
              return acc;
            }
          },
          0,
        );

        // Compare in integer cents so float error (0.1 + 0.2) can't wrongly
        // flag a balanced allocation as unbalanced.
        if (
          toCents(accountEntryData.startingBalance) !==
          toCents(sumOfFundStartingBalances)
        ) {
          console.error("Not balanced");
          validateStartingBalance({
            accountEntryData,
            setRemainingStartingToAssign,
            setAccountEntryErrors,
            setAccountEntryFeedback,
          });
          return;
        }
      }
      // Final validation before submission
      if (
        (!accountEntryData.subAccount ||
          !accountEntryData.subAccount === null ||
          accountEntryData.subAccountOf) &&
        accountEntryData.group !== "" &&
        (!accountEntryData.startingBalance ||
          accountEntryData.startingBalance === 0 ||
          accountEntryData.startingBalanceDate)
      ) {
        try {
          const result = await handleSubmit();
          // handleSubmit now returns { error } on a failure (e.g. duplicate
          // number) instead of an alert — surface it on the account-number field
          // and re-enable the form rather than leaving the spinner up.
          if (result?.error) {
            setAccountEntryErrors((prev) => ({ ...prev, accountNumber: true }));
            setAccountEntryFeedback((prev) => ({
              ...prev,
              accountNumber: result.error,
            }));
            reenableSubmit();
          }
        } catch (error) {
          // A thrown save error (infra failure, starting-balance guardrail)
          // must not leave the submit button stuck disabled. Surface a generic
          // message, re-enable, and report unexpected errors to Sentry.
          console.error("Error submitting account:", error);
          setAccountEntryErrors((prev) => ({ ...prev, accountNumber: true }));
          setAccountEntryFeedback((prev) => ({
            ...prev,
            accountNumber: "Failed to save the account. Please try again.",
          }));
          reenableSubmit();
          if (!isExpectedAccountError(error)) {
            Sentry.withScope((scope) => {
              scope.setTag("feature", "coa-account-save");
              scope.setTag("operation", editingAccountId ? "edit" : "create");
              scope.setContext("accountSaveContext", { orgId: org });
              Sentry.captureException(error);
            });
          }
        }
      } else {
        reenableSubmit();
      }
    };

    //Function to trigger adding an account from the parent component
    React.useImperativeHandle(ref, () => ({
      handleSubmit,
      validateForm,
    }));

    // console.log(db, org);

    return (
      <React.Fragment>
        {massAdd && <div style={{ height: "25px" }} />}
        {editingAccountId ? (
          <TitleText>Edit Account Details</TitleText>
        ) : (
          <React.Fragment>
            {!massAdd && (
              <TitleText style={{ fontSize: "1.8rem" }}>
                Add an Account
              </TitleText>
            )}
          </React.Fragment>
        )}
        {!disabledAddButton ? (
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: massAdd ? "row" : "column",
              flexWrap: massAdd ? "nowrap" : "wrap",
              alignItems: "center",
              gap: 2,
              pb: 3,
              pl: 2,
              pr: 2,
            }}
            noValidate
            autoComplete="off">
            <FormControl>
              <Tooltip
                placement="top-end"
                title={
                  editingAccountId
                    ? "Cannot Change an Existing Account's Type"
                    : "This is Immutable Upon Account Creation"
                }>
                <TextField
                  style={{
                    marginBottom: "20px",
                    width: massAdd ? "160px" : "350px",
                  }}
                  value={accountEntryData.type}
                  label="Account Type"
                  name="type"
                  color="secondary"
                  select
                  disabled={editingAccountId || options?.type}
                  onChange={handleChange}
                  required
                  onBlur={() => {
                    validateAccountType({
                      accountEntryData,
                      setAccountEntryErrors,
                      setAccountEntryFeedback,
                    });
                    if (accountEntryErrors.accountNumber) {
                      validateAccountNumber({
                        accountEntryData,
                        setAccountEntryErrors,
                        setAccountEntryFeedback,
                        accountDataToEdit,
                        existingAccountNumbers,
                      });
                    }
                  }}
                  error={accountEntryErrors.type}
                  helperText={accountEntryFeedback.type}>
                  {accountTypes.map((type) => (
                    <MenuItem key={`menu-${type}`} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Tooltip>
            </FormControl>
            <FormControl>
              <TextField
                sx={{
                  width:
                    massAdd &&
                    (accountEntryData.type === "Assets" ||
                      accountEntryData.type === "Liabilities")
                      ? "300px"
                      : "350px",
                  marginRight:
                    massAdd &&
                    (accountEntryData.type === "Assets" ||
                      accountEntryData.type === "Liabilities")
                      ? "20px"
                      : "0px",
                  fontSize: "22px",
                  marginBottom: "20px",
                }}
                required
                label="Account Name"
                name="accountName"
                variant="outlined"
                color="secondary"
                value={accountEntryData.accountName}
                onChange={handleChange}
                error={accountEntryErrors.accountName}
                helperText={accountEntryFeedback.accountName}
                onBlur={() => {
                  validateAccountName({
                    accountEntryData,
                    setAccountEntryErrors,
                    setAccountEntryFeedback,
                  });
                }}
              />
            </FormControl>
            <FormControl>
              <TextField
                sx={{
                  width: massAdd ? "140px" : "350px",
                  fontSize: "22px",
                  marginRight: massAdd ? "20px" : "0px",
                  marginLeft: massAdd ? "20px" : "0px",
                }}
                label="Account Number"
                name="accountNumber"
                variant="outlined"
                color="secondary"
                value={accountEntryData.accountNumber}
                onChange={handleChange}
                error={accountEntryErrors.accountNumber}
                helperText={accountEntryFeedback.accountNumber}
                required
                onBlur={() => {
                  validateAccountNumber({
                    accountEntryData,
                    setAccountEntryErrors,
                    setAccountEntryFeedback,
                    accountDataToEdit,
                    existingAccountNumbers,
                  });
                  if (accountEntryErrors.type) {
                    validateAccountType({
                      accountEntryData,
                      setAccountEntryErrors,
                      setAccountEntryFeedback,
                    });
                  }
                }}
                disabled={accountNumberToAdd ? true : false}
              />
            </FormControl>

            {(accountEntryData.type === "Assets" ||
              accountEntryData.type === "Liabilities") &&
              !massAdd && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <FormControlLabel
                    required
                    variant="outlined"
                    control={
                      <Checkbox
                        color="secondary"
                        checked={accountEntryData.isRegister}
                        name="isRegister"
                        onChange={handleChange}
                      />
                    }
                    label={
                      accountEntryData.type === "Assets"
                        ? "This is a bank account"
                        : "This is a credit card"
                    }
                  />

                  {accountEntryData.isRegister === true && (
                    <FormControlLabel
                      data-testid="foundation-account-form-toggle-illiquid"
                      variant="outlined"
                      control={
                        <Checkbox
                          color="secondary"
                          checked={accountEntryData.illiquid === true}
                          name="illiquid"
                          onChange={handleChange}
                          inputProps={{
                            "aria-label":
                              "Mark register as illiquid (exclude from runway)",
                          }}
                        />
                      }
                      label="Illiquid (exclude from runway)"
                    />
                  )}

                  {funds?.length > 0 && !accountDataToEdit && (
                    <StartingBalance
                      accountEntryData={accountEntryData}
                      setAccountEntryData={setAccountEntryData}
                      entryErrors={accountEntryErrors}
                      entryFeedback={accountEntryFeedback}
                    />
                  )}
                </div>
              )}

            {accounts?.length > 0 && (
              <React.Fragment>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}>
                  <Switch
                    checked={accountEntryData.subAccount}
                    color="secondary"
                    onChange={(event) => {
                      setAccountEntryData({
                        ...accountEntryData,
                        subAccount: event.target.checked,
                        subAccountOf: null,
                      });
                    }}
                    name="subAccount"
                    inputProps={{ "aria-label": "secondary checkbox" }}
                  />
                  <LineText>This is a Sub Account</LineText>
                </div>

                {accountEntryData.subAccount && !massAdd && (
                  <Autocomplete
                    key={`subAccount-${accountEntryData.accountNumber}-selector`}
                    id="subAccount"
                    name="subAccount"
                    value={accountEntryData.subAccountOf}
                    options={accounts.filter(
                      (account) =>
                        account.accountType.toUpperCase() ===
                          accountEntryData.type.toUpperCase() &&
                        !findIfSubAccountInHierarchy(
                          accountsHierarchy,
                          account.id,
                        ) &&
                        account.id !== editingAccountId,
                    )}
                    getOptionLabel={(option) =>
                      `${option.accountNumber} - ${option.accountName}`
                    }
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(event, newValue) => {
                      console.log("New Value: ", newValue);
                      let foundAccountGroup = null;
                      if (newValue !== null && newValue?.id) {
                        foundAccountGroup = findAccountsGroupInHierarchy(
                          accountsHierarchy,
                          newValue.id,
                        );
                      }

                      // Update the subAccountOf state
                      setAccountEntryData({
                        ...accountEntryData,
                        subAccountOf: newValue,
                        group: foundAccountGroup?.groupName || "",
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          width: massAdd ? "190px" : "350px",
                          fontSize: "22px",
                        }}
                        label="Sub Account of..."
                        name="subAccount"
                        color="secondary"
                        variant="outlined"
                        error={accountEntryErrors.subAccount}
                        helperText={accountEntryFeedback.subAccount}
                        disabled={!accountEntryData.subAccount}
                        required
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.accountName}
                      </li>
                    )}
                  />
                )}
              </React.Fragment>
            )}

            <Autocomplete
              freeSolo
              id="group"
              name="group"
              value={accountEntryData.group}
              onInputChange={(event, newValue) => {
                setAccountEntryData({
                  ...accountEntryData,
                  group: newValue,
                });
              }}
              options={
                !massAdd
                  ? (
                      accountsHierarchy?.types?.find(
                        (type) => type.type === accountEntryData.type,
                      )?.groups || []
                    ).map((option) => option.groupName)
                  : Object.keys(
                      groupsAddedToParent[accountEntryData.type] || {},
                    ).map((option) => option)
              }
              disabled={accountEntryData.subAccount || options?.group}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    width: massAdd ? "190px" : "350px",
                    fontSize: "22px",
                  }}
                  label="Group"
                  name="group"
                  color="secondary"
                  variant="outlined"
                  required
                  onBlur={() => {
                    if (massAdd) {
                      addGroup(
                        {
                          type: accountEntryData.type,
                          group: accountEntryData.group,
                        },
                        accountEntryData.accountNumber,
                      );
                    }
                    validateGroup({
                      accountEntryData,
                      setAccountEntryErrors,
                      setAccountEntryFeedback,
                    });
                  }}
                  error={accountEntryErrors.group}
                  helperText={accountEntryFeedback.group}
                  disabled={accountEntryData.subAccount || options?.group}
                />
              )}
            />
            {/* TODO: This is a dumb repitiion, need to refactor so that this is contained in a component that can be placed better by what component it's rendering in */}
            {(accountEntryData.type === "Assets" ||
              accountEntryData.type === "Liabilities") &&
              massAdd && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <FormControlLabel
                    required
                    variant="outlined"
                    control={
                      <Checkbox
                        color="secondary"
                        checked={accountEntryData.isRegister}
                        name="isRegister"
                        onChange={handleChange}
                      />
                    }
                    label={
                      accountEntryData.type === "Assets"
                        ? "This is a bank account"
                        : "This is a credit card"
                    }
                  />
                </div>
              )}
          </Box>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}>
            <LoadingLogoStyled />
            <LineText>Adding Account...</LineText>
          </div>
        )}
        {!massAdd && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
            }}>
            <Button
              data-testid="foundation-accounts-button-submit"
              variant="contained"
              disabled={Object.values(accountEntryErrors).some((err) => err)}
              onClick={handleClick}>
              {accountDataToEdit?.accountDatabaseId
                ? "Edit Account"
                : "Add Account"}
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  },
);

export default AddAccount;
